<script setup lang="ts">
/**
 * Notification panel opened from the bell of the top bar.
 *
 * Rendering follows the reference screen: one row per notification, a dot that
 * carries the read state, and a single "Tout marquer lu" action in the header.
 * A row is entirely clickable: it marks the notification as read, and when its
 * wording designates an element the advisor may reach, it closes the panel and
 * opens that element. Marking as read is not destructive, so it needs no
 * confirmation modal.
 *
 * The panel also renders the states of the interface contract that the static
 * reference does not carry: skeleton while loading, network banner with a
 * retry, and server card with the request identifier.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import { HzButton } from '@horizon/ui';
import type { CibleNotification } from '@horizon/api-client';
import type { Notification } from '@horizon/types';
import BandeauReseau from '@/composants/BandeauReseau.vue';
import { dateHeureFr } from '@/composables/useFormatsFr';
import { useToasts } from '@/composables/useToasts';
import { useNotificationsStore } from '@/stores/notifications';
import { useSessionStore } from '@/stores/session';

const props = defineProps<{ ouvert: boolean }>();

const emit = defineEmits<{
  fermer: [];
}>();

const notifications = useNotificationsStore();
const session = useSessionStore();
const router = useRouter();
const { succes } = useToasts();

const racine = ref<HTMLElement | null>(null);
const titre = ref<HTMLElement | null>(null);

function surClicDocument(evenement: MouseEvent): void {
  if (!props.ouvert) {
    return;
  }
  const cible = evenement.target;
  if (cible instanceof Node && racine.value?.contains(cible) === true) {
    return;
  }
  emit('fermer');
}

/** Screen that owns the designated element: its report list, or its client file. */
function destination(cible: CibleNotification): RouteLocationRaw {
  return cible.genre === 'document'
    ? { name: 'documents' }
    : { name: 'client', params: { id: cible.clientId } };
}

/**
 * A destination is only offered when the guards of the router would let the
 * signed in role through, so a notification never lands the advisor on a
 * redirect back to the dashboard.
 */
function estAccessible(vers: RouteLocationRaw): boolean {
  const resolue = router.resolve(vers);
  if (resolue.meta.ecriture === true && !session.peutEcrire) {
    return false;
  }
  const rolesAutorises = resolue.meta.roles;
  if (rolesAutorises === undefined) {
    return true;
  }
  const role = session.role;
  return role !== null && rolesAutorises.includes(role);
}

/** Element the row opens, or null when the notification designates nothing reachable. */
function cibleOuvrable(id: string): CibleNotification | null {
  const cible = notifications.cible(id);
  if (cible === null) {
    return null;
  }
  return estAccessible(destination(cible)) ? cible : null;
}

/** Accessible name of a row: its wording, then what activating it does. */
function libelleAction(notification: Notification): string {
  const cible = cibleOuvrable(notification.id);
  const ouverture = cible === null ? '' : ` Ouvrir : ${cible.libelle}.`;
  const lecture = notification.lu ? '' : ' Marquer comme lue.';
  return `${notification.titre}.${lecture}${ouverture}`;
}

async function activer(notification: Notification): Promise<void> {
  if (!notification.lu) {
    await notifications.marquerCommeLue(notification.id);
  }
  const cible = cibleOuvrable(notification.id);
  if (cible === null) {
    return;
  }
  emit('fermer');
  await router.push(destination(cible));
}

async function toutLire(): Promise<void> {
  const traitees = await notifications.toutMarquerCommeLu();
  if (traitees > 0) {
    succes(
      traitees === 1
        ? 'Notification marquée comme lue.'
        : `${traitees} notifications marquées comme lues.`,
    );
  }
}

watch(
  () => props.ouvert,
  async (ouvert) => {
    if (!ouvert) {
      return;
    }
    await notifications.charger();
    await nextTick();
    titre.value?.focus();
  },
);

onMounted(() => {
  document.addEventListener('mousedown', surClicDocument);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', surClicDocument);
});
</script>

<template>
  <section
    v-if="props.ouvert"
    ref="racine"
    class="panneau"
    aria-labelledby="hz-notifications-titre"
  >
    <header class="panneau__entete">
      <h2
        id="hz-notifications-titre"
        ref="titre"
        class="panneau__titre"
        tabindex="-1"
      >
        Notifications
      </h2>
      <button
        type="button"
        class="panneau__tout"
        @click="toutLire"
      >
        Tout marquer lu
      </button>
    </header>

    <BandeauReseau
      :erreur="notifications.erreurReseau"
      :en-cours="notifications.chargement"
      @reessayer="notifications.reessayer()"
    />

    <div
      v-if="notifications.chargement"
      class="panneau__chargement"
      aria-busy="true"
    >
      <span class="hz-skeleton hz-skeleton--titre" />
      <span class="hz-skeleton hz-skeleton--ligne" />
      <span class="hz-skeleton hz-skeleton--ligne" />
      <span class="hz-visually-hidden">Chargement des notifications</span>
    </div>

    <div
      v-else-if="notifications.erreurServeur !== null"
      class="hz-etat"
    >
      <p class="hz-etat__titre">
        Notifications indisponibles
      </p>
      <p>{{ notifications.erreurServeur.messageUtilisateur }}</p>
      <p class="hz-etat__requete">
        Référence à communiquer au support : {{ notifications.erreurServeur.requestId }}
      </p>
      <HzButton
        variant="secondary"
        size="sm"
        @click="notifications.reessayer()"
      >
        Réessayer
      </HzButton>
    </div>

    <p
      v-else-if="notifications.estVide"
      class="panneau__vide"
    >
      Aucune notification pour le moment.
    </p>

    <ul
      v-else
      class="panneau__liste"
    >
      <li
        v-for="notification in notifications.liste"
        :key="notification.id"
        class="panneau__item"
      >
        <button
          type="button"
          class="panneau__ligne"
          :aria-label="libelleAction(notification)"
          @click="activer(notification)"
        >
          <span
            class="panneau__pastille"
            :class="{ 'panneau__pastille--lue': notification.lu }"
            aria-hidden="true"
          />
          <span class="panneau__texte">
            <span
              class="panneau__item-titre"
              :class="{ 'panneau__item-titre--lue': notification.lu }"
            >{{ notification.titre }}</span>
            <span class="panneau__item-corps">{{ notification.corps }}</span>
            <span class="panneau__item-date">{{ dateHeureFr(notification.cree_le) }}</span>
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.panneau {
  position: absolute;
  top: calc(var(--hz-topbar-h) - var(--hz-sp-2));
  right: var(--hz-sp-5);
  z-index: 40;
  width: min(370px, calc(100vw - 2 * var(--hz-sp-3)));
  max-height: min(60vh, 460px);
  overflow-y: auto;
  border: 1px solid var(--hz-border);
  border-radius: var(--hz-radius-card);
  background: var(--hz-bg-card);
  box-shadow: var(--hz-shadow-pop);
  font-family: var(--hz-font);
}

.panneau__entete {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--hz-sp-3);
  padding: 13px var(--hz-sp-4);
  border-bottom: 1px solid var(--hz-border);
}

.panneau__titre {
  font-size: var(--hz-fs-md);
  font-weight: 700;
  color: var(--hz-text-strong);
}

.panneau__tout {
  padding: 0;
  border: none;
  border-radius: var(--hz-radius);
  background: none;
  color: var(--hz-primary-500);
  font-family: inherit;
  font-size: var(--hz-fs-sm);
  font-weight: 600;
  cursor: pointer;
}

.panneau__tout:hover {
  color: var(--hz-primary-700);
}

.panneau__tout:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}

.panneau__chargement {
  display: flex;
  flex-direction: column;
  gap: var(--hz-sp-3);
  padding: var(--hz-sp-4);
}

.panneau__vide {
  padding: 30px;
  color: var(--hz-text-muted);
  font-size: 12.5px;
  text-align: center;
}

.panneau__liste {
  margin: 0;
  padding: 0;
  list-style: none;
}

.panneau__item {
  border-bottom: 1px solid var(--hz-border);
}

.panneau__item:last-child {
  border-bottom: none;
}

.panneau__ligne {
  display: flex;
  gap: 10px;
  width: 100%;
  padding: 11px var(--hz-sp-4);
  border: none;
  background: none;
  color: var(--hz-text-strong);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.panneau__ligne:hover {
  background: var(--hz-row-hover);
}

.panneau__ligne:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}

.panneau__pastille {
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: var(--hz-radius-pill);
  background: var(--hz-primary-500);
}

.panneau__pastille--lue {
  background: var(--hz-border);
}

.panneau__texte {
  flex: 1;
  min-width: 0;
}

.panneau__item-titre {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

.panneau__item-titre--lue {
  font-weight: 500;
}

.panneau__item-corps {
  display: block;
  margin-top: 2px;
  color: var(--hz-text-muted);
  font-size: var(--hz-fs-sm);
  line-height: 1.45;
}

.panneau__item-date {
  display: block;
  margin-top: 4px;
  color: var(--hz-text-muted);
  font-size: 11px;
}

@media (max-width: 479px) {
  .panneau {
    right: var(--hz-sp-3);
  }
}
</style>
